import {Center, themeDark, themeLight} from '@resurs-internal/mui';
import {isRestError} from './services/rest-client/RestError';
import {persistKeys} from './hooks/useAuthState/useAuthState';
import {createSyncStoragePersister} from '@tanstack/query-sync-storage-persister';
import {
  DehydrateOptions,
  MutationCache,
  QueryCache,
  QueryClient,
  useIsRestoring,
} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import {PersistQueryClientProvider} from '@tanstack/react-query-persist-client';
import {ResursLoading} from 'components';
import React, {PropsWithChildren, ReactNode, Suspense} from 'react';
import {AppRouter} from './AppRouter';
import {captureException, ErrorBoundary} from '@sentry/react';
import {DialogProvider} from './hooks/useDialog/DialogProvider';
import {LocalizationProvider} from '@mui/x-date-pickers';
import {CssBaseline, ThemeProvider, useMediaQuery} from '@mui/material';
import {ErrorPage} from './pages';
import {AppSnackbarProvider} from './services/snackbar';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import {init as initApm} from '@elastic/apm-rum';
import {config} from './config/index';

if (
  ['local', 'dev', 'integration', 'prod'].includes(config.apm.apmEnvironment)
) {
  initApm({
    serviceName: config.apm.apmServiceName,
    serverUrl: config.apm.apmServerUrl,
    environment: config.apm.apmEnvironment,
  });
}

const onError = (error: Error) => {
  if (!isRestError(error)) return captureException(error);

  const fingerprint = [`${error.status} ${error.method} ${error.url}`];
  captureException(error, {
    fingerprint: fingerprint,
  });
};

// Garbage collection time
const gcTime = 1000 * 60 * 60 * 24; // 24 hours
const staleTime = 1000 * 60 * 5; // 5 minutes
const queryClient = new QueryClient({
  queryCache: new QueryCache({onError}),
  mutationCache: new MutationCache({onError}),
  defaultOptions: {queries: {gcTime, staleTime}},
});

export const persister = createSyncStoragePersister({
  storage: window.localStorage,
});

const dehydrateOptions: DehydrateOptions = {
  shouldDehydrateQuery: (qry) => {
    if (window.localStorage == null) {
      return false;
    }
    return persistKeys.some((key) => {
      return qry.queryKey.join('/') === key.join('/');
    });
  },
};

export const App = () => {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{persister, dehydrateOptions}}>
      <PersistGate
        fallback={
          <Center>
            <ResursLoading />
          </Center>
        }>
        <RBThemeProvider>
          <ErrorBoundary
            fallback={({error, componentStack, eventId}) => (
              <ErrorPage
                error={error as Error}
                componentStack={componentStack}
                eventId={eventId}
              />
            )}>
            <AppSnackbarProvider>
              <DialogProvider>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Suspense
                    fallback={
                      <Center>
                        <ResursLoading />
                      </Center>
                    }>
                    <AppRouter />
                  </Suspense>
                </LocalizationProvider>
              </DialogProvider>
            </AppSnackbarProvider>
          </ErrorBoundary>
        </RBThemeProvider>
      </PersistGate>
      <ReactQueryDevtools initialIsOpen={false} />
    </PersistQueryClientProvider>
  );
};

const PersistGate: React.FC<PropsWithChildren<{fallback: ReactNode}>> = ({
  children,
  fallback,
}) => {
  const isRestoring = useIsRestoring();

  return <>{isRestoring ? fallback : children}</>;
};

const RBThemeProvider: React.FC<React.PropsWithChildren> = ({children}) => {
  const deviceTheme = useMediaQuery('(prefers-color-scheme: dark)')
    ? 'dark'
    : 'light';

  const theme = deviceTheme === 'dark' ? themeDark : themeLight;
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};
